import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconNew, IconUpload, IconScan, IconSuccess, IconError } from '../../icons';
import Card from '../common/Card';
import Button from '../common/Button';
import { useReceivedInvoiceUpload } from '../receivedInvoices/useReceivedInvoiceUpload';
import type { ReceivedInvoiceUploadMeta } from '../receivedInvoices/useReceivedInvoiceUpload';
import { useScanCapture } from './useScanCapture';
import type { ParsedZugferdData } from '../../types/receivedInvoiceTypes';

/**
 * `inbox/CaptureBar` (M-V3-C, architecture-ux-density-v3.md §5) — a single
 * thin "Beleg hinzufügen" row that replaces the two oversized Erfassungs-Karten
 * (`UploadStreamCard` + `ScanCaptureCard`) in the Unified Inbox. It reuses the
 * SHARED upload/scan hooks (`useReceivedInvoiceUpload`/`useScanCapture`), so the
 * Loop-A weiche and the KI-scan path keep byte-identical behaviour — only the
 * chrome shrank.
 *
 * Gate-frei by design (§5.6): the page owns the entitlement decision and passes
 * `scanEntitled`/`scanFlagOn`; this component just renders the correct scan slot
 * (real capture / one-click Premium teaser / nothing).
 *
 * @module components/inbox/CaptureBar
 */
export interface CaptureBarProps {
  /** Loop-A IPA path success (flag off) — parent writes `received_invoices`. */
  onUploadSuccess: (data: ParsedZugferdData, meta: ReceivedInvoiceUploadMeta) => void;
  /** Loop-A server-WORM path success (flag on) — parent shows archive toast + refresh. */
  onInboundUploadSuccess: (result: { inboundDocumentId: string; deferred: boolean }) => void;
  /** Foto/Scan extraction success — parent refreshes the list. */
  onCaptured: () => void;
  /** User may actually use the Foto/Scan capture (renders the real "Foto" button). */
  scanEntitled: boolean;
  /** Scan flag is on but the user isn't entitled (renders the one-click Premium teaser). */
  scanFlagOn: boolean;
  /**
   * M13a-GA: Ultra-/Beta-Nutzer ist scan-berechtigt, hat aber noch keine gültige
   * AVV-Zustimmung. Der „Foto"-Klick öffnet dann NICHT den File-Picker, sondern
   * ruft {@link onScanRequiresConsent} (Parent öffnet den AVV-Dialog). Bewusst
   * required, damit der Compiler jeden Consumer zwingt, das Gate zu verdrahten.
   */
  scanConsentPending: boolean;
  /** Foto-Klick bei `scanConsentPending` — Parent öffnet den AVV-Dialog. */
  onScanRequiresConsent: () => void;
}

export const CaptureBar: React.FC<CaptureBarProps> = ({
  onUploadSuccess,
  onInboundUploadSuccess,
  onCaptured,
  scanEntitled,
  scanFlagOn,
  scanConsentPending,
  onScanRequiresConsent,
}) => {
  const { t } = useTranslation('posteingang');
  const navigate = useNavigate();
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const scanInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const {
    processFile,
    loading: uploadLoading,
    error: uploadError,
    accept: uploadAccept,
  } = useReceivedInvoiceUpload({ onUploadSuccess, onInboundUploadSuccess });
  const { processImage, uploading: scanUploading, error: scanError, success: scanSuccess } =
    useScanCapture({ onCaptured });

  const openUploadPicker = () => {
    if (!uploadLoading) uploadInputRef.current?.click();
  };
  const openScanPicker = () => {
    if (scanUploading) return;
    // M13a-GA: fehlt die AVV-Zustimmung, öffnet „Foto" NICHT den File-Picker,
    // sondern den Zustimmungs-Dialog (Parent). Kein Storage-Upload vor Consent.
    if (scanConsentPending) {
      onScanRequiresConsent();
      return;
    }
    scanInputRef.current?.click();
  };

  const handleUploadChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await processFile(file);
    if (uploadInputRef.current) uploadInputRef.current.value = '';
  };

  const handleScanChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (scanInputRef.current) scanInputRef.current.value = '';
    if (file) await processImage(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    // Drop always routes to the Upload path (§5.7): desktop drops are files;
    // the Foto path is mobile capture only. Ignore drops while a file is
    // already being processed.
    if (uploadLoading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void processFile(file);
  };

  // Upload errors take precedence; scan errors surface when there is no upload
  // error. Success is scan-only (the Upload path shows its toast via the page).
  const error = uploadError ?? scanError;
  const success = scanSuccess;

  return (
    <div data-testid="unified-inbox-capture-bar">
      <Card
        padded={false}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`px-4 py-2.5 transition-shadow ${isDragging ? 'ring-1 ring-indigo-500' : ''}`}
      >
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-primary inline-flex items-center gap-2">
            <IconNew className="text-accent-ink" size={14} />
            {t('unifiedInbox.captureBar.title', 'Beleg hinzufügen')}
          </span>
          <span className="text-xs text-muted hidden md:inline">
            {t('unifiedInbox.captureBar.dropHint', 'PDF oder Bild hierher ziehen — oder:')}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="xs"
              variant="secondary"
              icon={<IconUpload size={12} />}
              loading={uploadLoading}
              onClick={openUploadPicker}
              data-testid="capture-bar-upload"
            >
              {t('unifiedInbox.captureBar.uploadAction', 'Datei hochladen')}
            </Button>
            {scanEntitled ? (
              <Button
                size="xs"
                variant="secondary"
                icon={<IconScan size={12} />}
                loading={scanUploading}
                onClick={openScanPicker}
                data-testid="capture-bar-scan"
              >
                {t('unifiedInbox.captureBar.scanAction', 'Foto')}
              </Button>
            ) : scanFlagOn ? (
              <Button
                size="xs"
                variant="secondary"
                icon={<IconScan size={12} />}
                onClick={() => navigate('/dashboard/abonnement')}
                title={t('receiptScan.teaserTitle', { ns: 'common', defaultValue: 'KI-Beleg-Scanner' })}
                data-testid="capture-bar-scan-teaser"
              >
                {t('unifiedInbox.captureBar.scanTeaserAction', 'Foto (Premium)')}
              </Button>
            ) : null}
          </div>
        </div>

        <input
          ref={uploadInputRef}
          type="file"
          accept={uploadAccept}
          className="hidden"
          aria-hidden="true"
          onChange={handleUploadChange}
          disabled={uploadLoading}
        />
        <input
          ref={scanInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          aria-hidden="true"
          onChange={handleScanChange}
          disabled={scanUploading}
        />
      </Card>

      {error && (
        <div
          className="mt-3 flex items-start gap-2 bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg p-3 text-sm text-[var(--tone-danger-fg)]"
          data-testid="capture-bar-error"
        >
          <IconError className="mt-0.5 flex-shrink-0" size={14} />
          <span>{error}</span>
        </div>
      )}
      {success && !error && (
        <div
          className="mt-3 flex items-start gap-2 bg-[var(--tone-success-bg)] border border-[var(--tone-success-border)] rounded-lg p-3 text-sm text-[var(--tone-success-fg)]"
          data-testid="capture-bar-success"
        >
          <IconSuccess className="mt-0.5 flex-shrink-0" size={14} />
          <span>{success}</span>
        </div>
      )}
    </div>
  );
};

export default CaptureBar;
